<template>
    <div class="main-page designer-workflow-page">
        <InfoPopup v-model="showInfoPopup" :title="infoTitle" @confirm="handleConfirm" @cancel="handleCancel">
            <p>{{ infoMessage }}</p>
        </InfoPopup>

        <header class="workflow-header">
            <div>
                <p class="eyebrow">Voice Designer</p>
                <h1>Choose your base voice</h1>
                <p>Start with a voice collection sample, uploaded audio, or a fresh recording.</p>
            </div>
            <div class="info-btn-wrapper" @mouseenter="showInfo = true" @mouseleave="showInfo = false">
                <button class="info-btn" type="button" aria-label="Voice designer guidance">i</button>
                <div v-if="showInfo" class="info-tooltip">
                    {{ UserGuideTexts['choose-base-voice'] }}
                </div>
            </div>
        </header>

        <section class="designer-stepper" aria-label="Voice design progress">
            <article class="step-card current">
                <span class="step-number">1</span>
                <span><strong>Choose source</strong><small>Base voice or upload</small></span>
            </article>
            <article class="step-card">
                <span class="step-number">2</span>
                <span><strong>Shape voice</strong><small>Prompt and generate</small></span>
            </article>
            <article class="step-card">
                <span class="step-number">3</span>
                <span><strong>Save</strong><small>Name and tag</small></span>
            </article>
        </section>

        <div class="workflow-grid">
            <section class="source-panel">
                <div class="source-grid" aria-label="Base voice source">
                    <button class="source-card" :class="{ selected: selectedSource === 'collection' }" type="button"
                        @click="selectSource('collection')">
                        <span class="source-icon">V</span>
                        <span><strong>Voice collection</strong><small>Browse built-in and saved voices.</small></span>
                    </button>
                    <button class="source-card" :class="{ selected: selectedSource === 'upload' }" type="button"
                        @click="selectSource('upload')">
                        <span class="source-icon">U</span>
                        <span><strong>Upload</strong><small>Use a clean source sample.</small></span>
                    </button>
                    <button class="source-card" :class="{ selected: selectedSource === 'record' }" type="button"
                        @click="selectSource('record')">
                        <span class="source-icon">R</span>
                        <span><strong>Record</strong><small>Capture audio in the workflow.</small></span>
                    </button>
                </div>

                <div v-if="selectedTab === 'collection'" class="voice-collection">
                    <VGVoiceExplorerModal :items="allVoices" title="Select a Voice" placeholder="Search by voice name…"
                        label-key="voice_name" :search-keys="['voice_name', 'name', 'voice']"
                        @select="selectVoiceFromPicker" v-model:searchText="searchText" />
                </div>
                <div v-else class="upload-record source-panel-inner">
                    <RecordUploadView @audio-blob="handleAudioBlob" />
                    <div v-if="isRecording" class="recording-visuals">
                        <canvas ref="waveCanvas" width="600" height="100"></canvas>
                        <span class="record-timer">{{ formatTime(recordTime) }}</span>
                    </div>
                </div>
            </section>

            <aside class="selected-base-panel">
                <h2>Selected base</h2>
                <div class="selected-voice" v-if="currentVoice">
                    <div class="voice-avatar">{{ currentVoice.voice_name?.charAt(0) || 'V' }}</div>
                    <div>
                        <strong>{{ currentVoice.voice_name }}</strong>
                        <span>{{ selectedTab === 'collection' ? 'Preview ready' : 'Custom audio ready' }}</span>
                    </div>
                </div>
                <div class="selected-voice empty" v-else>
                    <div class="voice-avatar">?</div>
                    <div>
                        <strong>No base selected</strong>
                        <span>Pick, upload, or record to continue.</span>
                    </div>
                </div>
                <div class="audio-strip" v-if="audioUrl">
                    <audio class="footer-audio" v-if="selectedTab !== 'collection'" :src="audioUrl" controls></audio>
                    <div class="mini-wave" aria-hidden="true">
                        <i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i>
                    </div>
                    <span class="duration-note">Ready</span>
                </div>
                <ul class="voice-tags" v-if="currentVoice?.tags?.length">
                    <li v-for="tag in currentVoice.tags" :key="tag">{{ tag }}</li>
                </ul>
                <p class="panel-note">The selected base stays visible while users browse, so the next action is always clear.</p>
            </aside>
        </div>

        <footer class="page-footer">
            <div class="footer-summary">
                <div class="voice-avatar">{{ currentVoice?.voice_name?.charAt(0) || 'V' }}</div>
                <div>
                    <strong>{{ currentVoice ? currentVoice.voice_name : 'Choose a base voice' }}</strong>
                    <span>{{ currentVoice ? 'Step 1 ready: continue to prompting.' : 'Select a source before continuing.' }}</span>
                </div>
            </div>
            <div class="footer-actions">
                <button class="secondary-btn" type="button" @click="cancel">Cancel</button>
                <button class="primary-btn" type="button" :class="{ disabled: !audioUrl }" @click="onNextClick">
                    Next
                </button>
            </div>
        </footer>
    </div>
</template>

<script>
import { useRouter } from 'vue-router';
import { inject, ref, onBeforeUnmount, nextTick, onMounted } from 'vue'
import { getVoicesByUserId, getUserVoicesByUserId } from '@/api/services'
import { downloadDemoFile } from '@/api/files';
import VGVoiceExplorerModal from '@/components/common/VGVoiceExplorerModal.vue';
import UserGuideTexts from '@/data/UserGuideTexts';
import InfoPopup from '@/components/common/InfoPopup.vue';
import RecordUploadView from '@/components/common/RecordUploadView.vue';
import IconMic from '@/assets/icons/iconMic.svg';
import Union1 from '@/assets/figs/designer/Union1.png';
import Union2 from '@/assets/figs/designer/Union2.png';
import Union3 from '@/assets/figs/designer/Union3.png';
import Union4 from '@/assets/figs/designer/Union4.png';
import Union5 from '@/assets/figs/designer/Union5.png';
import Union6 from '@/assets/figs/designer/Union6.png';

export default {
    name: 'ChooseBaseVoice',
    components: { InfoPopup, VGVoiceExplorerModal, RecordUploadView },
    setup() {
        const router = useRouter();
        const waveCanvas = ref(null);
        let audioCtx = null;
        let analyser = null;
        let dataArray = null;
        let sourceNode = null;
        let rafId = null;
        const showInfo = ref(false);
        const userVoices = ref([]);
        let infoTimeout = null;
        const mediaStream = ref(null);
        const mediaRecorder = ref(null);
        const searchText = ref('');

        async function loadUserVoices() {
            try {
                userVoices.value = await getUserVoicesByUserId();
                if (userVoices.value.length === 0) {
                    showInfo.value = true;
                    infoTimeout = setTimeout(() => {
                        showInfo.value = false;
                    }, 10000);
                }
            } catch (err) {
                console.error('Failed to load voices:', err);
            }
        }

        function hideInfoTooltip() {
            showInfo.value = false;
            if (infoTimeout) clearTimeout(infoTimeout);
        }

        onMounted(() => {
            loadUserVoices();
            document.addEventListener('click', hideInfoTooltip);
            document.addEventListener('play', trackAudioPlayback, true);
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', hideInfoTooltip);
            document.removeEventListener('play', trackAudioPlayback, true);
            if (infoTimeout) clearTimeout(infoTimeout);
        });

        function drawWaveform() {
            if (!waveCanvas.value) return;
            rafId = requestAnimationFrame(drawWaveform);
            analyser.getByteTimeDomainData(dataArray);
            const canvas = waveCanvas.value;
            const ctx = canvas.getContext('2d');
            const { width, height } = canvas;
            ctx.clearRect(0, 0, width, height);
            ctx.lineWidth = 2;
            ctx.beginPath();
            const sliceWidth = width / dataArray.length;
            let x = 0;
            for (let i = 0; i < dataArray.length; i++) {
                const v = dataArray[i] / 128.0;
                const y = (v * height) / 2;
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
                x += sliceWidth;
            }
            ctx.stroke();
        }
        const sharedData = inject('sharedData')
        const trackedAudios = new Set();

        function trackAudioPlayback(event) {
            if (event.target instanceof HTMLAudioElement) {
                trackedAudios.add(event.target);
            }
        }

        const showInfoPopup = ref(false);
        const infoTitle = ref('');
        const infoMessage = ref('');
        const pendingAction = ref(null);

        const fileInput = ref(null);
        const audioUrl = ref('');
        const currentVoice = ref();

        async function handleAudioBlob(blob) {
            audioUrl.value = URL.createObjectURL(blob);
            currentVoice.value = {
                voice_name: 'Uploaded/Recorded Voice',
                tags: ['Custom', 'Uploaded/Recorded']
            };
            sharedData.value.baseAudioBlob = blob;
        }

        const isRecording = ref(false);
        const isDemoLoading = ref(false);
        const selectedTab = ref('collection');
        const selectedSource = ref('collection');
        const chunks = [];
        const recordTime = ref(0);
        let recorderTimerInterval = null;

        const allVoices = ref([]);

        loadVoices();

        // Helper for MM:SS formatting
        function formatTime(sec) {
            const m = Math.floor(sec / 60).toString().padStart(2, '0');
            const s = Math.floor(sec % 60).toString().padStart(2, '0');
            return `${m}:${s}`;
        }

        async function trimAudioBlob(blob) {
            const arrayBuffer = await blob.arrayBuffer();
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
            const maxDur = 29;
            const targetSampleRate = 24000;

            function writeString(view, offset, str) {
                for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
            }
            function bufferToWav(buffer) {
                const numChan = buffer.numberOfChannels;
                const length = buffer.length * numChan * 2 + 44;
                const view = new DataView(new ArrayBuffer(length));
                writeString(view, 0, 'RIFF');
                view.setUint32(4, 36 + buffer.length * numChan * 2, true);
                writeString(view, 8, 'WAVE');
                writeString(view, 12, 'fmt ');
                view.setUint32(16, 16, true);
                view.setUint16(20, 1, true);
                view.setUint16(22, numChan, true);
                view.setUint32(24, buffer.sampleRate, true);
                view.setUint32(28, buffer.sampleRate * numChan * 2, true);
                view.setUint16(32, numChan * 2, true);
                view.setUint16(34, 16, true);
                writeString(view, 36, 'data');
                view.setUint32(40, buffer.length * numChan * 2, true);
                let offset = 44;
                for (let i = 0; i < buffer.length; i++) {
                    for (let ch = 0; ch < numChan; ch++) {
                        let s = buffer.getChannelData(ch)[i];
                        s = Math.max(-1, Math.min(1, s));
                        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
                        offset += 2;
                    }
                }
                return new Blob([view], { type: 'audio/wav' });
            }

            const duration = Math.min(audioBuffer.duration, maxDur);
            const startOffset = audioBuffer.duration > maxDur ? audioBuffer.duration - maxDur : 0;
            const frameCount = Math.ceil(duration * targetSampleRate);
            const offlineCtx = new OfflineAudioContext(1, frameCount, targetSampleRate);
            const src = offlineCtx.createBufferSource();
            src.buffer = audioBuffer;
            src.connect(offlineCtx.destination);
            src.start(0, startOffset, duration);
            const rendered = await offlineCtx.startRendering();
            return bufferToWav(rendered);
        }
        async function loadVoices() {
            try {
                allVoices.value = await getVoicesByUserId();
                console.log(allVoices.value)
            } catch (err) {
                console.error('Failed to load voices:', err);
            }
        }
        function selectVoiceFromPicker(voice) {
            currentVoice.value = voice;
            currentVoice.value.tags = [voice.accent, voice.gender];
            downloadDemoAudio(voice)
        }
        function selectSource(source) {
            selectedSource.value = source;
            selectedTab.value = source === 'collection' ? 'collection' : 'upload';
        }
        async function downloadDemoAudio(voice) {
            isDemoLoading.value = true;
            try {
                const data = await downloadDemoFile('Kokoro v1.0', voice.voice_name);
                const wavBlob = new Blob([data], { type: 'audio/wav' });
                const trimmedBlob = await trimAudioBlob(wavBlob);
                audioUrl.value = URL.createObjectURL(trimmedBlob);
                sharedData.value.baseAudioBlob = trimmedBlob;
            } catch (err) {
                console.error('Download demo voice error: ', err);
            } finally {
                isDemoLoading.value = false;
            }
        }

        function onNextClick() {
            if (audioUrl.value) {
                infoTitle.value = 'Notice';
                infoMessage.value = 'Using this voice as the base for your new voice design.';
                pendingAction.value = 'next';
                showInfoPopup.value = true;
            }
        }
        function onUploadClick() {
            if (audioUrl.value) {
                infoTitle.value = 'Warning';
                infoMessage.value = 'You have already recorded or uploaded audio. This action will delete existing audio. Are you sure?';
                pendingAction.value = 'upload';
                showInfoPopup.value = true;
            } else {
                fileInput.value.click();
            }
        }
        function onRecordClick() {
            if (isRecording.value) {
                toggleRecording();
                return;
            }
            if (audioUrl.value) {
                infoTitle.value = 'Warning';
                infoMessage.value = 'You have already recorded or uploaded audio. This action will delete existing audio. Are you sure?';
                pendingAction.value = 'record';
                showInfoPopup.value = true;
            } else {
                toggleRecording();
            }
        }

        function handleConfirm() {
            if (pendingAction.value === 'upload') {
                fileInput.value.click();
            } else if (pendingAction.value === 'record') {
                toggleRecording();
            } else if (pendingAction.value === 'next') {
                goNext();
            }
            pendingAction.value = null;
            showInfoPopup.value = false;
        }

        function handleCancel() {
            pendingAction.value = null;
            showInfoPopup.value = false;
        }

        function cancel() {
            router.push({ name: 'voice-designer' });
        }
        function goNext() {
            sharedData.value.baseVoiceInfo = currentVoice.value
            sharedData.value.baseAudioUrl = audioUrl.value;
            stopAllAudio()
            router.push({ name: 'text-prompting', });
        }

        async function onFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                const trimmedBlob = await trimAudioBlob(file);
                audioUrl.value = URL.createObjectURL(trimmedBlob);
                currentVoice.value = {
                    voice_name: 'Uploaded Voice',
                    tags: ['Custom', 'Uploaded']
                };
                sharedData.value.baseAudioBlob = trimmedBlob;
                const reader = new FileReader();
                reader.onload = async () => {
                    const arrayBuffer = reader.result;
                    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
                    console.log('Uploaded Stats.', 'Sample rate:', audioBuffer.sampleRate, 'Channels:', audioBuffer.numberOfChannels, 'Samples:', audioBuffer.length, 'Duration (s):', audioBuffer.duration)
                };
                reader.readAsArrayBuffer(trimmedBlob);
            }
        }

        async function toggleRecording() {
            if (!isRecording.value) {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                mediaStream.value = stream;
                audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                analyser = audioCtx.createAnalyser();
                analyser.fftSize = 2048;
                dataArray = new Uint8Array(analyser.fftSize);
                sourceNode = audioCtx.createMediaStreamSource(stream);
                sourceNode.connect(analyser);
                isRecording.value = true;
                await nextTick();
                drawWaveform();
                mediaRecorder.value = new MediaRecorder(stream);
                mediaRecorder.value.ondataavailable = e => chunks.push(e.data);
                mediaRecorder.value.onstop = async () => {
                    const blob = new Blob(chunks);
                    const trimmedBlob = await trimAudioBlob(blob);
                    audioUrl.value = URL.createObjectURL(trimmedBlob);

                    currentVoice.value = { voice_name: 'Recorded Voice', tags: ['Custom', 'Recorded'] };
                    console.log(trimmedBlob.type);
                    sharedData.value.baseAudioBlob = trimmedBlob;
                    chunks.length = 0;
                };
                mediaRecorder.value.start();
                recordTime.value = 0;
                recorderTimerInterval = setInterval(() => recordTime.value++, 1000);
            } else {
                mediaRecorder.value.stop();
                if (mediaStream.value) {
                    mediaStream.value.getTracks().forEach(track => track.stop());
                    mediaStream.value = null;
                }
                cancelAnimationFrame(rafId);
                clearInterval(recorderTimerInterval);
                recordTime.value = 0;
                isRecording.value = false;
            }
        }

        onBeforeUnmount(() => {
            cancelAnimationFrame(rafId);
            // Removed: if (audioCtx) { audioCtx.close(); }
            if (mediaRecorder.value && mediaRecorder.value.state !== 'inactive') {
                mediaRecorder.value.stop();
            }
            if (mediaStream.value) {
                mediaStream.value.getTracks().forEach(track => track.stop());
                mediaStream.value = null;
            }
        });
        function stopAllAudio() {
            trackedAudios.forEach(audio => audio.pause());
            trackedAudios.clear();
            document.querySelectorAll('audio').forEach(el => el.pause());
        }
        return {
            handleAudioBlob,
            waveCanvas,
            cancel,
            goNext,
            fileInput,
            audioUrl,
            isRecording,
            toggleRecording,
            onFileChange,
            mediaStream,
            onUploadClick,
            onRecordClick,
            handleConfirm,
            handleCancel,
            pendingAction,
            showInfoPopup,
            infoTitle,
            infoMessage,
            onNextClick,
            allVoices,
            selectVoiceFromPicker,
            isDemoLoading,
            selectedTab,
            selectedSource,
            selectSource,
            currentVoice,
            recordTime,
            formatTime,
            showInfo,
            UserGuideTexts,
            Union1,
            Union2,
            Union3,
            Union4,
            Union5,
            Union6,
            IconMic, searchText
        };
    }
}
</script>

<style scoped>
.designer-workflow-page {
    min-height: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    color: #111827;
    background:
        linear-gradient(120deg, rgba(124, 58, 237, 0.08), transparent 28%),
        linear-gradient(240deg, rgba(37, 99, 235, 0.07), transparent 30%),
        #f7f8fc;
    overflow: auto;
}

.workflow-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    padding: 20px 28px 14px;
}

.eyebrow {
    margin: 0 0 6px;
    color: #7c3aed;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.workflow-header h1 {
    margin: 0;
    font-size: 26px;
    line-height: 1.15;
}

.workflow-header p,
.panel-note,
.selected-voice span,
.footer-summary span,
.step-card small,
.source-card small {
    color: #5f6b7d;
}

.workflow-header p {
    margin: 6px 0 0;
}

.info-btn-wrapper {
    position: relative;
}

.info-btn {
    width: 32px;
    height: 32px;
    border: 1px solid #dfe5ef;
    border-radius: 50%;
    color: #7c3aed;
    background: #fff;
    cursor: pointer;
}

.info-tooltip {
    position: absolute;
    right: 0;
    top: 40px;
    width: min(360px, 80vw);
    z-index: 20;
    padding: 12px 14px;
    color: #5f6b7d;
    white-space: pre-line;
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 16px 40px rgba(17, 24, 39, 0.12);
}

.designer-stepper {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    padding: 0 28px 18px;
}

.step-card {
    display: grid;
    grid-template-columns: 30px minmax(0, 1fr);
    gap: 10px;
    align-items: center;
    padding: 12px;
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.82);
}

.step-card.current {
    border-color: #c4b5fd;
    background: linear-gradient(135deg, #ffffff, #f8f5ff);
    box-shadow: inset 0 0 0 1px rgba(196, 181, 253, 0.45);
}

.step-number {
    width: 30px;
    height: 30px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    color: #7c3aed;
    background: #f3f0ff;
    font-weight: 700;
}

.step-card strong,
.step-card small,
.source-card strong,
.source-card small {
    display: block;
}

.workflow-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 340px;
    gap: 20px;
    align-items: start;
    padding: 0 28px 96px;
}

.source-panel,
.selected-base-panel {
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.88);
    box-shadow: 0 10px 34px rgba(17, 24, 39, 0.06);
}

.source-panel {
    min-width: 0;
    padding: 18px;
}

.source-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 16px;
}

.source-card {
    min-height: 112px;
    padding: 14px;
    text-align: left;
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.86);
    cursor: pointer;
    transition: background 180ms ease, border-color 180ms ease, transform 180ms ease;
}

.source-card:hover,
.source-card.selected {
    border-color: #c4b5fd;
    background: #fbfaff;
    transform: translateY(-1px);
}

.source-icon {
    width: 34px;
    height: 34px;
    display: grid;
    place-items: center;
    margin-bottom: 10px;
    border-radius: 7px;
    color: #7c3aed;
    background: #f3f0ff;
    font-weight: 700;
}

.voice-collection,
.source-panel-inner {
    min-width: 0;
}

.selected-base-panel {
    position: sticky;
    top: 20px;
    display: grid;
    gap: 14px;
    padding: 18px;
}

.selected-base-panel h2 {
    margin: 0;
    font-size: 18px;
}

.selected-voice,
.footer-summary {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.voice-avatar {
    width: 42px;
    height: 42px;
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    border-radius: 7px;
    color: #7c3aed;
    background: linear-gradient(135deg, #ede9fe, #dbeafe);
    font-weight: 760;
    text-transform: uppercase;
}

.selected-voice strong,
.footer-summary strong {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.selected-voice span,
.footer-summary span {
    display: block;
    margin-top: 2px;
    font-size: 13px;
}

.audio-strip {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px;
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: #f8fafc;
}

audio.footer-audio {
    width: min(100%, 220px);
    border-radius: 4px;
}

.mini-wave {
    height: 34px;
    display: flex;
    align-items: center;
    gap: 3px;
}

.mini-wave i {
    display: block;
    width: 4px;
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(124, 58, 237, 0.82), rgba(37, 99, 235, 0.4));
}

.mini-wave i:nth-child(1) { height: 9px; }
.mini-wave i:nth-child(2) { height: 18px; }
.mini-wave i:nth-child(3) { height: 13px; }
.mini-wave i:nth-child(4) { height: 25px; }
.mini-wave i:nth-child(5) { height: 17px; }
.mini-wave i:nth-child(6) { height: 29px; }
.mini-wave i:nth-child(7) { height: 12px; }
.mini-wave i:nth-child(8) { height: 22px; }
.mini-wave i:nth-child(9) { height: 15px; }
.mini-wave i:nth-child(10) { height: 26px; }

.duration-note,
.panel-note {
    color: #5f6b7d;
    font-size: 13px;
}

.voice-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    list-style: none;
    margin: 0;
    padding: 0;
}

.voice-tags li {
    border: 1px solid #dfe5ef;
    border-radius: 999px;
    padding: 4px 8px;
    color: #475569;
    background: #f8fafc;
    font-size: 13px;
}

.upload-record {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.recording-visuals {
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
}

.record-timer {
    color: #7c3aed;
    font-weight: 600;
}

.page-footer {
    position: sticky;
    bottom: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-height: 72px;
    padding: 12px 28px;
    margin-top: auto;
    border-top: 1px solid #dfe5ef;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(14px);
}

.footer-actions {
    display: flex;
    gap: 10px;
    flex: 0 0 auto;
}

.primary-btn,
.secondary-btn {
    min-height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 9px 14px;
    border-radius: 7px;
    cursor: pointer;
    transition: background 180ms ease, border-color 180ms ease, transform 180ms ease;
}

.primary-btn {
    border: 0;
    color: white;
    background: linear-gradient(135deg, #7c3aed, #6d5dfc);
    box-shadow: 0 12px 28px rgba(109, 93, 252, 0.18);
}

.secondary-btn {
    border: 1px solid #dfe5ef;
    color: #111827;
    background: #fff;
}

.primary-btn:not(.disabled):hover,
.secondary-btn:hover {
    transform: translateY(-1px);
}

.primary-btn.disabled {
    opacity: 0.45;
    cursor: not-allowed;
    pointer-events: none;
}

button:focus-visible {
    outline: 3px solid rgba(124, 58, 237, 0.24);
    outline-offset: 2px;
}

@media (max-width: 1120px) {
    .workflow-grid {
        grid-template-columns: 1fr;
    }

    .selected-base-panel {
        position: static;
    }
}

@media (max-width: 760px) {
    .workflow-header,
    .page-footer {
        align-items: stretch;
        flex-direction: column;
    }

    .designer-stepper,
    .source-grid {
        grid-template-columns: 1fr;
    }

    .designer-stepper,
    .workflow-grid {
        padding-left: 16px;
        padding-right: 16px;
    }

    .workflow-header,
    .page-footer {
        padding-left: 16px;
        padding-right: 16px;
    }
}
</style>
